import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';

export const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d47n1pfjsic9gsa4ulsg', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section - Hidden on mobile */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h4 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Stay Updated with Luxury
          </h4>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get exclusive access to new yacht listings, special offers, and luxury maritime experiences
          </p>
          
          <form onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter_subscription" className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-white text-sm"
              />
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-mail-send-line"></i>
                )}
              </Button>
            </div>
            
            {submitStatus === 'success' && (
              <p className="mt-3 text-green-200">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="mt-3 text-red-200">Something went wrong. Please try again.</p>
            )}
          </form>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Pacifico, serif' }}>
                Step in luxury
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Step into a world of unparalleled luxury with our exclusive portfolio of yachts, apartments, commercial buildings, beach houses, and premium furniture across Nigeria.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Services</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Luxury Yacht Sales</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Premium Apartments</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Commercial Buildings</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Beach House Rentals</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Luxury Furniture</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Property Management</a></li>
              </ul>
            </div>

            {/* Locations */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Locations</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Lagos Marina</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Victoria Island</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Lekki Peninsula</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Abuja Central</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Port Harcourt</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Calabar</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Contact</h4>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-blue-400 mt-1 mr-3"></i>
                  <div className="text-gray-400">
                    <p>Step in luxury Headquarters</p>
                    <p>Plot 1234, Victoria Island</p>
                    <p>Lagos, Nigeria</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <i className="ri-phone-line text-blue-400 mr-3"></i>
                  <a href="tel:+234-800-STEP-LUXURY" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    +234 (800) STEP-LUXURY
                  </a>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line text-blue-400 mr-3"></i>
                  <a href="mailto:info@stepinluxury.ng" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    info@stepinluxury.ng
                  </a>
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line text-blue-400 mr-3"></i>
                  <span className="text-gray-400">Mon-Sat: 9AM-6PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 Step in luxury. All rights reserved.
              </div>
              <div className="text-gray-400 text-sm flex items-center space-x-4">
                <a href="/privacy" className="hover:text-white">Privacy Policy</a>
                <a href="/terms" className="hover:text-white">Terms</a>
              </div>
            </div>
        </div>
      </div>
    </footer>
  );
};
